import React, { useState } from "react";
import { Row, Col, Tabs, Typography } from "antd";

import { W0, W1 } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { type OrganizationPublicKey } from "@api/types";

import CodeBlock from "./CodeBlock";
import getIdTokenSnippet from "./helpers/getIdTokenSnippet";

const LABEL_KEY_ID = "Key ID (kid header)";
const LABEL_ISSUER = "Issuer (iss claim)";
const LABEL_EMAIL = "Email (email claim)";
const LABEL_SUBJECT = "Correlation ID (sub claim)";
const LABEL_PUBLIC_KEY = "Public key";
const LABEL_SNIPPET = "Issue an ID token";
const LABEL_EMPTY = "-";
const DESCRIPTION = "Your client signs each ID token with the private key that pairs with this public key. The kid header must be this key ID, and the iss claim must be this issuer. The sub claim and the email claim give the user that your client acts as. The example below uses your own user.";

const COLUMN_SPAN = 12;

const TAB_PUBLIC_KEY = "publicKey";
const TAB_SNIPPET = "snippet";

type Props = {
  publicKey: OrganizationPublicKey;
};

const OrganizationPublicKeyDetails = ({ publicKey }: Props): JSX.Element => {
  const { identity } = useAppContext() as AppContext;

  const [ activeTabKey, setActiveTabKey ] = useState(TAB_SNIPPET);

  const { id, issuer, publicKey: publicKeyPem } = publicKey;

  const snippet = getIdTokenSnippet({
    email: identity?.email,
    domain: identity?.organization?.domain,
    issuer,
    publicKeyId: id,
    correlationId: identity?.correlationId,
  });

  const labelStyle = {
    marginBottom: W0 / 2,
  };

  const valueStyle = {
    marginBottom: W1,
    wordBreak: "break-all" as const,
  };

  const renderValue = (label: string, value?: string) => {
    const hasValue = !!value;

    return (
      <Col span={COLUMN_SPAN}>
        <Typography.Paragraph type="secondary" style={labelStyle}>
          {label}
        </Typography.Paragraph>

        {hasValue
          ? (
            <Typography.Paragraph
              code
              copyable={{ text: value }}
              style={valueStyle}
            >
              {value}
            </Typography.Paragraph>
          )
          : (
            <Typography.Paragraph type="secondary" style={valueStyle}>
              {LABEL_EMPTY}
            </Typography.Paragraph>
          )}
      </Col>
    );
  };

  const codeByTabKey = {
    [TAB_SNIPPET]: snippet,
    [TAB_PUBLIC_KEY]: publicKeyPem,
  };

  const activeCode = codeByTabKey[activeTabKey];

  const tabItems = [
    {
      key: TAB_SNIPPET,
      label: LABEL_SNIPPET,
      children: <CodeBlock code={snippet} />,
    },
    {
      key: TAB_PUBLIC_KEY,
      label: LABEL_PUBLIC_KEY,
      children: <CodeBlock code={publicKeyPem} />,
    },
  ];

  // NOTE: the copy control keeps the copied state of the tab that it was on,
  //       therefore the key makes a new control for each tab.
  const tabBarExtraContent = {
    right: (
      <Typography.Text
        key={activeTabKey}
        copyable={{ text: activeCode }}
      />
    ),
  };

  return (
    <>
      <Typography.Paragraph type="secondary">
        {DESCRIPTION}
      </Typography.Paragraph>

      <Row gutter={W1}>
        {renderValue(LABEL_KEY_ID, id)}
        {renderValue(LABEL_ISSUER, issuer)}
        {renderValue(LABEL_EMAIL, identity?.email)}
        {renderValue(LABEL_SUBJECT, identity?.correlationId)}
      </Row>

      <Tabs
        size="small"
        items={tabItems}
        activeKey={activeTabKey}
        tabBarExtraContent={tabBarExtraContent}
        onChange={setActiveTabKey}
      />
    </>
  );
};

export default OrganizationPublicKeyDetails;
